import { Injectable } from '@angular/core';
import Echo from 'laravel-echo';
import Pusher from 'pusher-js';
import { Observable, Subject } from 'rxjs';
import { environment } from 'src/environments/environment';

declare global {
  interface Window {
    Pusher: any;
    Echo: any;
  }
}

@Injectable({
  providedIn: 'root'
})
export class EchoService {
  private echo: any = null;
  private isConnected: boolean = false;
  private notificationSubject = new Subject<any>();
  private userChannel: any = null;

  constructor() {
    this.initializeEcho();
  }

  /**
   * Initialize Laravel Echo with Pusher
   */
  private initializeEcho(): void {
    console.log('🔧 [WebSocket] Checking prerequisites...');

    // Validate environment configuration
    if (!environment.websocket) {
      console.error('✗ [WebSocket] Missing websocket configuration in environment');
      this.isConnected = false;
      return;
    }

    try {
      // Validate auth token
      const token = this.getAuthToken();
      if (!token) {
        console.error('✗ [WebSocket] Missing auth token in localStorage (key: "token")');
        this.isConnected = false;
        return;
      }
      console.log(`✓ [WebSocket] Auth token found: ${token.substring(0, 20)}...`);

      // Make Pusher globally available
      window.Pusher = Pusher;
      console.log('✓ [WebSocket] Pusher loaded');

      // Log WebSocket connection details
      const wsUrl = `ws${environment.websocket.forceTLS ? 's' : ''}://${environment.websocket.host}:${environment.websocket.port}`;
      console.log(`🔌 [WebSocket] Connecting to: ${wsUrl}`);
      console.log(`📡 [WebSocket] App key: ${environment.websocket.key}`);
      console.log(`🔐 [WebSocket] Auth endpoint: ${environment.apiEndPoint}/broadcasting/auth`);

      // Initialize Echo
      this.echo = new Echo({
        broadcaster: 'pusher',
        key: environment.websocket.key,
        wsHost: environment.websocket.host,
        wsPort: environment.websocket.port,
        cluster: environment.websocket.cluster,
        forceTLS: environment.websocket.forceTLS,
        encrypted: environment.websocket.encrypted,
        disableStats: environment.websocket.disableStats,
        enabledTransports: environment.websocket.enabledTransports as any,
        authEndpoint: `${environment.apiEndPoint}/broadcasting/auth`,
        auth: {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json'
          }
        }
      });

      // Store Echo globally
      window.Echo = this.echo;
      this.isConnected = true;

      console.log('✓ [WebSocket] Laravel Echo initialized successfully');
    } catch (error) {
      console.error('✗ [WebSocket] Error initializing Laravel Echo:', error);
      this.isConnected = false;
    }
  }

  /**
   * Get authentication token from localStorage
   */
  private getAuthToken(): string {
    const token = localStorage.getItem('token');
    return token || '';
  }

  /**
   * Get Echo instance
   */
  getEcho(): any {
    if (!this.echo) {
      this.initializeEcho();
    }
    return this.echo;
  }

  /**
   * Check if Echo is connected
   */
  isEchoConnected(): boolean {
    return this.isConnected && this.echo !== null;
  }

  /**
   * Listen to a channel
   */
  channel(channelName: string): any {
    if (!this.echo) {
      console.error('Echo is not initialized');
      return null;
    }
    return this.echo.channel(channelName);
  }

  /**
   * Listen to a private channel
   */
  private(channelName: string): any {
    if (!this.echo) {
      console.error('Echo is not initialized');
      return null;
    }
    return this.echo.private(channelName);
  }

  /**
   * Listen to a presence channel
   */
  join(channelName: string): any {
    if (!this.echo) {
      console.error('Echo is not initialized');
      return null;
    }
    return this.echo.join(channelName);
  }

  /**
   * Leave a channel
   */
  leave(channelName: string): void {
    if (this.echo) {
      this.echo.leave(channelName);
    }
  }

  /**
   * Leave all channels
   */
  leaveAllChannels(): void {
    if (this.echo) {
      this.echo.disconnect();
    }
  }

  /**
   * Reconnect Echo
   */
  reconnect(): void {
    if (this.echo) {
      this.echo.disconnect();
    }
    this.initializeEcho();
  }

  /**
   * Disconnect Echo
   */
  disconnect(): void {
    if (this.echo) {
      this.echo.disconnect();
      this.echo = null;
      this.isConnected = false;
      console.log('Laravel Echo disconnected');
    }
  }

  /**
   * Listen to user notification channel
   * This is the centralized approach for receiving all notifications
   *
   * @param userId - The authenticated user's ID
   * @returns Observable of notification events
   */
  listenToUserNotifications(userId: number): Observable<any> {
    console.log(`📡 [WebSocket] Attempting to subscribe to user channel (ID: ${userId})`);

    if (!this.echo || !this.isConnected) {
      console.error('✗ [WebSocket] Echo is not initialized or not connected');
      console.error('✗ [WebSocket] Make sure auth token exists in localStorage before subscribing');
      return this.notificationSubject.asObservable();
    }

    // Disconnect from previous user channel if exists
    if (this.userChannel) {
      const oldChannelName = `App.Models.User.${userId}`;
      console.log(`🔄 [WebSocket] Disconnecting from previous channel: ${oldChannelName}`);
      this.leave(oldChannelName);
      this.userChannel = null;
    }

    // Subscribe to user's private notification channel
    const channelName = `App.Models.User.${userId}`;
    console.log(`🔐 [WebSocket] Subscribing to private channel: ${channelName}`);

    try {
      this.userChannel = this.private(channelName);

      if (this.userChannel) {
        // Add subscription success event listener
        this.userChannel.subscribed(() => {
          console.log(`✅ [WebSocket] Channel subscription successful: ${channelName}`);
          console.log(`✅ [WebSocket] Backend authorization successful for user ${userId}`);
        });

        // Add subscription error event listener
        this.userChannel.error((error: any) => {
          console.error(`❌ [WebSocket] Channel subscription error: ${channelName}`, error);
          console.error(`❌ [WebSocket] This usually means Laravel channel authorization failed in routes/channels.php`);
          console.error(`❌ [WebSocket] Check Laravel logs for authorization callback errors`);

          // Log detailed error information
          if (error.type === 'AuthError') {
            console.error(`❌ [WebSocket] Authentication Error: Check JWT token validity`);
            console.error(`❌ [WebSocket] Token being sent: Bearer ${this.getAuthToken().substring(0, 20)}...`);
          }
        });

        // Listen for Laravel's broadcast notification event
        this.userChannel.notification((notification: any) => {
          console.log('📨 [WebSocket] Notification received:', notification);
          this.notificationSubject.next(notification);
        });

        console.log(`✓ [WebSocket] Successfully subscribed to channel: ${channelName}`);
        console.log('✓ [WebSocket] Listening for .notification() events from Laravel');
        console.log('⏳ [WebSocket] Waiting for backend authorization response...');
      } else {
        console.error(`✗ [WebSocket] Failed to subscribe to channel: ${channelName}`);
      }
    } catch (error) {
      console.error(`✗ [WebSocket] Error subscribing to channel ${channelName}:`, error);
    }

    return this.notificationSubject.asObservable();
  }

  /**
   * Stop listening to user notifications
   */
  stopListeningToUserNotifications(userId: number): void {
    if (this.userChannel) {
      this.leave(`App.Models.User.${userId}`);
      this.userChannel = null;
      console.log('Stopped listening to user notifications');
    }
  }
}
